import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { alternatesOf, langOfPath } from '../content';
import { CHROME, HOME, LANG_NAMES } from '../content/chrome';

// Vers la même page dans l'autre langue si elle existe, sinon vers la page
// principale de cette langue.
const LanguageSwitch = ({ lang, path, className, onNavigate }) => {
  const alternates = alternatesOf(path) || {};
  return (
    <div role="group" className={`flex items-center gap-3 text-xs font-semibold tracking-wide ${className || ''}`} aria-label={CHROME[lang].languages}>
      {Object.keys(HOME).map((l) =>
        l === lang ? (
          <span key={l} className="text-ink" aria-current="true">{l.toUpperCase()}</span>
        ) : (
          <Link
            key={l}
            to={alternates[l] || HOME[l]}
            hrefLang={l}
            lang={l}
            title={LANG_NAMES[l]}
            onClick={onNavigate}
            className="text-ink-faint hover:text-ink transition-colors"
          >
            {l.toUpperCase()}
          </Link>
        )
      )}
    </div>
  );
};

const Header = () => {
  const { pathname } = useLocation();
  const lang = langOfPath(pathname);
  const t = CHROME[lang];
  const navItems = t.nav;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setIsMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-paper/95 backdrop-blur-sm transition-shadow duration-200 ${
      isScrolled ? 'border-b border-line' : 'border-b border-transparent'
    }`}>
      <nav className="container mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link
            to={HOME[lang]}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <img
              src="/logo512.png"
              alt={t.logoAlt}
              className="w-8 h-8 object-contain"
            />
            <div className="flex flex-col leading-none">
              <span className="font-serif text-xl font-semibold text-ink">Expomis</span>
              <span className="text-[11px] tracking-wide text-ink-faint">{t.tagline}</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end
                className={({ isActive }) =>
                  `text-sm transition-colors ${
                    isActive ? 'text-ink font-medium' : 'text-ink-soft hover:text-ink'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <LanguageSwitch lang={lang} path={pathname} />
            <Link
              to={t.cta.to}
              className="px-6 py-3 bg-ink text-white text-sm font-medium hover:bg-brand-deep transition-colors"
            >
              {t.cta.label}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-ink"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={t.menu}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-6 border-t border-line flex flex-col gap-1">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end
                onClick={() => setIsMenuOpen(false)}
                className={({ isActive }) =>
                  `px-2 py-3 text-sm ${isActive ? 'text-ink font-medium' : 'text-ink-soft'}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <LanguageSwitch lang={lang} path={pathname} className="px-2 py-3" onNavigate={() => setIsMenuOpen(false)} />
            <Link
              to={t.cta.to}
              onClick={() => setIsMenuOpen(false)}
              className="mt-3 px-6 py-3 bg-ink text-white text-sm font-medium text-center"
            >
              {t.cta.label}
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
